import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Router, NavigationEnd } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { filter } from 'rxjs/operators';

interface PageSeo {
  title: string;
  description: string;
  keywords?: string;
  ogImage?: string;
  canonicalPath?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  private readonly baseUrl = 'https://continuum.com';
  private readonly defaultImage = 'https://customer-assets.emergentagent.com/job_trust-design-1/artifacts/zjh02rg0_logo.png';
  private readonly siteName = 'Continuum';
  
  private readonly pageSeoData: { [key: string]: PageSeo } = {
    '/': {
      title: 'Continuum',
      description: 'Continuum helps you create a comprehensive estate plan to protect your loved ones. Store important documents, record messages for your family, and ensure they have guidance when they need it most.',
      keywords: 'estate planning, legacy planning, family protection, document storage, digital vault, end of life planning'
    },
    '/login': {
      title: 'Sign In | Continuum',
      description: 'Sign in to your Continuum account to manage your estate plan, access documents, and update your beneficiary information.',
      keywords: 'continuum login, estate planning login, sign in'
    },
    '/getting-started': {
      title: 'Get Started | Continuum - Create Your Legacy Profile',
      description: 'Create your Continuum account in minutes. Start building your legacy profile with secure document storage, voice messages, and beneficiary management.',
      keywords: 'create account, sign up, estate planning signup, get started'
    },
    '/forgot-password': {
      title: 'Reset Password | Continuum',
      description: 'Reset your Continuum account password. We\'ll send you a secure link to create a new password.',
      keywords: 'forgot password, reset password, account recovery'
    },
    '/dashboard': {
      title: 'Dashboard | Continuum',
      description: 'Manage your estate plan from your personal Continuum dashboard. Access documents, update beneficiaries, and track your legacy profile completion.',
      keywords: 'dashboard, estate plan management, beneficiaries'
    },
    '/profile': {
      title: 'Profile Settings | Continuum',
      description: 'Manage your Continuum profile settings, subscription, and account security.',
      keywords: 'profile settings, account settings, subscription'
    },
    '/admin/login': {
      title: 'Admin Portal | Continuum',
      description: 'Continuum administrator portal login.',
      keywords: 'admin login, administrator'
    },
    '/admin/dashboard': {
      title: 'Admin Dashboard | Continuum',
      description: 'Continuum administrator dashboard.',
      keywords: 'admin dashboard, user management'
    },
    '/admin/users': {
      title: 'User Management | Continuum Admin',
      description: 'Manage Continuum users and subscriptions.',
      keywords: 'user management, admin users'
    }
  };

  constructor(
    private meta: Meta,
    private title: Title,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.initRouteListener();
  }

  private initRouteListener(): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.updateSeoForRoute(event.urlAfterRedirects);
    });
  }

  updateSeoForRoute(url: string): void {
    // Get base path without query params or fragments
    const path = url.split('?')[0].split('#')[0];
    const seoData = this.pageSeoData[path] || this.pageSeoData['/'];
    
    this.updateTags(seoData, path);
  }

  updateTags(seo: PageSeo, path: string): void {
    // Update title
    this.title.setTitle(seo.title);
    
    // Update meta tags
    this.meta.updateTag({ name: 'title', content: seo.title });
    this.meta.updateTag({ name: 'description', content: seo.description });
    
    if (seo.keywords) {
      this.meta.updateTag({ name: 'keywords', content: seo.keywords });
    }
    
    // Open Graph
    this.meta.updateTag({ property: 'og:title', content: seo.title });
    this.meta.updateTag({ property: 'og:description', content: seo.description });
    this.meta.updateTag({ property: 'og:url', content: `${this.baseUrl}${path}` });
    this.meta.updateTag({ property: 'og:image', content: seo.ogImage || this.defaultImage });
    
    // Twitter
    this.meta.updateTag({ name: 'twitter:title', content: seo.title });
    this.meta.updateTag({ name: 'twitter:description', content: seo.description });
    this.meta.updateTag({ name: 'twitter:url', content: `${this.baseUrl}${path}` });
    this.meta.updateTag({ name: 'twitter:image', content: seo.ogImage || this.defaultImage });
    
    // Update canonical URL
    if (isPlatformBrowser(this.platformId)) {
      this.updateCanonicalUrl(`${this.baseUrl}${seo.canonicalPath || path}`);
    }
  }

  private updateCanonicalUrl(url: string): void {
    const existingLink = document.querySelector('link[rel="canonical"]');
    if (existingLink) {
      existingLink.setAttribute('href', url);
    } else {
      const link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      link.setAttribute('href', url);
      document.head.appendChild(link);
    }
  }

  // Custom method for dynamic pages
  setCustomTags(title: string, description: string, keywords?: string): void {
    this.updateTags({
      title: `${title} | ${this.siteName}`,
      description,
      keywords
    }, this.router.url);
  }
}
